// Guardas de rutas (UX §2.3, §3, §15): sesión, restricciones, local de la URL, rol y módulo de la sección.
import { useEffect, useRef, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { BookingLocationsResponse, SessionResponse } from '@atte/contracts';
import { api, isApiError, publicApi } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { defaultLocation, homePathFor, sessionQuery, useSession } from '@/lib/session';
import { useSyncThemeFromProfile } from '@/lib/theme';
import { roleHasSection, sectionEnabled, SECTION_MODULE, type SectionKey } from '@/lib/permissions';
import { fill, t } from '@/lib/i18n/es-AR';
import { sectionLabel } from './shell/nav';
import { BootError, BootScreen } from './pages';
import { ModuleOffState, RedirectWithToast } from './gates';

/** Ruta actual para `?volver=`. */
function currentPath(loc: { pathname: string; search: string }) {
  return `${loc.pathname}${loc.search}`;
}

/** `/login?volver=…` (sin aviso de sesión vencida: primer ingreso). */
export function loginPath(returnTo?: string, expired = false): string {
  const sp = new URLSearchParams();
  if (returnTo && returnTo !== '/' && !returnTo.startsWith('/login')) sp.set('volver', returnTo);
  if (expired) sp.set('motivo', 'vencida');
  const qs = sp.toString();
  return `/login${qs ? `?${qs}` : ''}`;
}

type Restriction = 'password' | 'mfaSetup';

/**
 * Exige sesión. Muestra el arranque (y su error, UX §15.1), redirige al ingreso sin sesión y aplica las
 * sesiones restringidas (cambio de contraseña / 2FA obligatoria, API §1.4). `allow` deja pasar la pantalla
 * que resuelve esa restricción.
 */
export function RequireSession({ allow, children }: { allow?: Restriction; children?: ReactNode }) {
  const q = useQuery(sessionQuery);
  const location = useLocation();
  useSyncThemeFromProfile(q.data);

  if (q.error && isApiError(q.error) && q.error.status === 401) {
    if (q.error.reason === 'mfa_pending') return <Navigate to="/login/2fa" replace />;
    // Si ya había sesión en pantalla, venció; si no, es el primer ingreso.
    return <Navigate to={loginPath(currentPath(location), !!q.data)} replace />;
  }
  if (!q.data) {
    if (q.error) return <BootError error={q.error} retrying={q.isFetching} onRetry={() => void q.refetch()} />;
    return <BootScreen />;
  }

  const s = q.data;
  if (s.restrictions.passwordChangeRequired && allow !== 'password') return <Navigate to="/primer-acceso" replace />;
  if (!s.restrictions.passwordChangeRequired && s.restrictions.mfaSetupRequired && allow !== 'mfaSetup') {
    return <Navigate to="/configurar-2fa" replace />;
  }
  // Las pantallas de restricción no tienen sentido sin la restricción: van a la app.
  if (allow === 'password' && !s.restrictions.passwordChangeRequired) return <Navigate to={homePathFor(s)} replace />;
  if (allow === 'mfaSetup' && !s.restrictions.mfaSetupRequired && !s.restrictions.passwordChangeRequired) {
    return <Navigate to={homePathFor(s)} replace />;
  }
  return <>{children ?? <Outlet />}</>;
}

/** `/app` sin local: al último local abierto (o "Sin acceso"). */
export function AppIndexRedirect() {
  const s = useSession();
  const loc = defaultLocation(s);
  return <Navigate to={loc ? `/app/${loc.slug}/agenda` : '/sin-acceso'} replace />;
}

/**
 * Valida el slug de la URL contra los locales del usuario y recuerda el último local en el perfil
 * (UX §2.4, UX-P51).
 */
export function RequireLocation({ children }: { children?: ReactNode }) {
  const s = useSession();
  const { locationSlug } = useParams();
  const loc = s.locations.find((l) => l.slug === locationSlug);
  useRememberLocation(s, loc?.id);

  if (s.locations.length === 0) return <Navigate to="/sin-acceso" replace />;
  if (!loc) {
    const fallback = defaultLocation(s);
    return <RedirectWithToast to={fallback ? `/app/${fallback.slug}/agenda` : '/sin-acceso'} message={t.errors.noAccessLocation} />;
  }
  return <>{children ?? <Outlet />}</>;
}

function useRememberLocation(s: SessionResponse, locationId: string | undefined) {
  const qc = useQueryClient();
  const sent = useRef<string | null>(null);
  useEffect(() => {
    if (!locationId || s.viewAs || locationId === s.user.lastLocationId || sent.current === locationId) return;
    sent.current = locationId;
    api
      .patch('/me/preferences', { lastLocationId: locationId })
      .then(() => {
        qc.setQueryData<SessionResponse>(qk.session, (old) =>
          old ? { ...old, user: { ...old.user, lastLocationId: locationId } } : old,
        );
      })
      .catch(() => {
        /* preferencia: si falla, se reintenta la próxima vez */
      });
  }, [qc, locationId, s.viewAs, s.user.lastLocationId]);
}

/**
 * Sección de la app: el rol tiene que tenerla y su módulo estar prendido (UX §2.2, §15.4, UX-P29).
 * Sin permiso → Agenda con aviso; módulo apagado → estado "{Módulo} está apagado".
 */
export function SectionGate({ section, children }: { section: SectionKey; children: ReactNode }) {
  const s = useSession();
  const { locationSlug } = useParams();
  const role = s.viewAs?.role ?? s.user.role;
  if (!roleHasSection(role, section)) {
    return (
      <RedirectWithToast
        to={`/app/${locationSlug}/agenda`}
        message={fill(t.errors.noAccessSection, { section: sectionLabel(section, s.business.vocab) })}
      />
    );
  }
  const mod = SECTION_MODULE[section];
  if (mod && !sectionEnabled(section, s.modules)) return <ModuleOffState module={mod} />;
  return <>{children}</>;
}

/** Ajustes: el dueño ve todo; encargado y profesional solo Mi cuenta (UX §12.1). */
export function SettingsGate({ children }: { children: ReactNode }) {
  const s = useSession();
  const { locationSlug } = useParams();
  const role = s.viewAs?.role ?? s.user.role;
  if (role !== 'owner') return <Navigate to={`/app/${locationSlug}/ajustes/mi-cuenta`} replace />;
  return <>{children}</>;
}

/** "Primeros pasos": solo el dueño mientras el negocio no terminó la puesta en marcha (UX §3.9). */
export function OwnerSetupGate({ children }: { children: ReactNode }) {
  const s = useSession();
  if (s.user.role !== 'owner' || s.business.setupCompleted) return <Navigate to={homePathFor(s)} replace />;
  return <>{children}</>;
}

/**
 * `/` (UX §2.3): con sesión → la app; sin sesión → la página pública si hay un solo local con reservas, la
 * elección de local si hay varios, o el ingreso.
 */
export function EntryRedirect() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await qc.fetchQuery(sessionQuery);
        if (!cancelled) navigate(homePathFor(s), { replace: true });
        return;
      } catch (e) {
        if (isApiError(e) && e.status === 401 && e.reason === 'mfa_pending') {
          if (!cancelled) navigate('/login/2fa', { replace: true });
          return;
        }
      }
      try {
        const pub = await publicApi.get<BookingLocationsResponse>('/booking-locations', { skipGlobalHandlers: true });
        if (cancelled) return;
        if (pub.redirectSlug) navigate(`/r/${pub.redirectSlug}`, { replace: true });
        else if (pub.items.length > 1) navigate('/r', { replace: true });
        else navigate('/login', { replace: true });
      } catch {
        if (!cancelled) navigate('/login', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, qc]);
  return <BootScreen />;
}
